import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import ValueRows from "@/components/ValueRows";
import ArsaBinaValueRows from "@/components/ArsaBinaValueRows";
import {
  DETAIL_SCHEMAS,
  BUILDING_FIELDS,
  VARIANT_STYLES,
  isMoneyField,
  formatThousands,
  unformatThousands,
} from "@/lib/detailSchemas";

const VALUE_ROWS_TYPES = ["mesken", "ofis", "is_yeri", "arsa", "tarla", "bahce"];
const ARSA_BINA_TYPES = ["arsa_bina", "fabrika"];

export default function DetailFields({ propertyType, details, onChange, filter }) {
  const schema = DETAIL_SCHEMAS[propertyType];
  if (!schema) return null;

  // filter: "structural" → only variant="default" sections, no buildings/floors/summary
  //         "areaValue"  → area+value UIs + buildings list
  //         undefined    → render everything (backward compatible)
  const showStructural = !filter || filter === "structural";
  const showAreaValue = !filter || filter === "areaValue";
  const usesValueRows = VALUE_ROWS_TYPES.includes(propertyType);
  const usesArsaBina = ARSA_BINA_TYPES.includes(propertyType);
  const sectionsToRender = schema.sections.filter((s) => {
    if (filter === "structural") return s.variant === "default";
    if (filter === "areaValue") return false; // area/value handled by dedicated components
    return true;
  });

  const setField = (key, value) => onChange({ ...details, [key]: value });

  const setBuilding = (idx, key, value) => {
    const buildings = [...(details.buildings || [])];
    buildings[idx] = { ...buildings[idx], [key]: value };
    onChange({ ...details, buildings });
  };
  const addBuilding = () =>
    onChange({
      ...details,
      buildings: [
        ...(details.buildings || []),
        {
          bina_adi: "",
          yapi_sinifi: "",
          kat_sayisi: "",
          alan: "",
          amortisman_orani: "",
          insaat_seviyesi: "",
        },
      ],
    });
  const removeBuilding = (idx) => {
    const buildings = [...(details.buildings || [])];
    buildings.splice(idx, 1);
    onChange({ ...details, buildings });
  };

  return (
    <div className="space-y-4">
      {/* Formula-based Area × Unit = Total rows */}
      {usesValueRows && showAreaValue && (
        <ValueRows propertyType={propertyType} details={details} onChange={onChange} />
      )}
      {usesArsaBina && showAreaValue && (
        <ArsaBinaValueRows details={details} onChange={onChange} />
      )}

      {sectionsToRender.map((section, sIdx) => {
        const style = VARIANT_STYLES[section.variant] || VARIANT_STYLES.default;
        const cols = section.variant === "default" ? "grid-cols-2" : "grid-cols-4";
        return (
          <div
            key={sIdx}
            className={`rounded-sm border ${style.card} p-3`}
            data-testid={`section-${section.variant}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className={`h-3 w-1 rounded-sm ${style.accent}`} />
              <div className={`text-[10px] uppercase tracking-widest font-bold ${style.header}`}>
                {section.title}
              </div>
            </div>
            <div className={`grid ${cols} gap-2`}>
              {section.fields.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={details[f.key] ?? ""}
                  onChange={(v) => setField(f.key, v)}
                  style={style}
                  dense
                />
              ))}
            </div>
          </div>
        );
      })}

      {schema.buildings && showAreaValue && (
        <div className="rounded-sm border border-violet-200 bg-violet-50/50 p-3.5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="h-3 w-1 rounded-sm bg-violet-500" />
              <div className="text-[11px] uppercase tracking-widest font-bold text-violet-800">
                Binalar
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addBuilding}
              className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-100 hover:text-violet-800"
              data-testid="add-building-btn"
            >
              <Plus className="h-3 w-3 mr-1" />
              Bina Ekle
            </Button>
          </div>
          {(details.buildings || []).length === 0 ? (
            <div className="text-xs text-violet-600/70 py-2">Henüz bina eklenmedi.</div>
          ) : (
            <div className="space-y-3">
              {(details.buildings || []).map((b, idx) => (
                <div
                  key={idx}
                  className="border border-violet-200 rounded-sm p-3 bg-white"
                  data-testid={`building-card-${idx}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-violet-900">Bina #{idx + 1}</div>
                    <button
                      type="button"
                      onClick={() => removeBuilding(idx)}
                      className="text-zinc-400 hover:text-red-600"
                      aria-label="Bina kaldır"
                      data-testid={`remove-building-${idx}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {BUILDING_FIELDS.map((bf) => (
                      <FieldRow
                        key={bf.key}
                        field={bf}
                        value={b[bf.key] ?? ""}
                        onChange={(v) => setBuilding(idx, bf.key, v)}
                        style={VARIANT_STYLES.default}
                        dense
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldRow({ field, value, onChange, style, dense = false }) {
  const inputCls = `rounded-sm bg-white ${dense ? "h-9" : ""} ${style?.inputRing || ""}`;
  if (field.type === "textarea") {
    return (
      <div className="space-y-1 col-span-2">
        <Label className="text-[10px] font-semibold text-zinc-700">{field.label}</Label>
        <Textarea
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className="rounded-sm resize-none bg-white"
          data-testid={`detail-${field.key}`}
        />
      </div>
    );
  }
  // Money fields render as formatted text ("1.234.567") but store raw digits.
  const isMoney = isMoneyField(field.key);
  const displayValue = isMoney ? formatThousands(value) : value;
  const handleChange = isMoney
    ? (e) => onChange(unformatThousands(e.target.value))
    : (e) => onChange(e.target.value);
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-semibold text-zinc-700">{field.label}</Label>
      <div className="relative">
        <Input
          type={isMoney ? "text" : field.type}
          inputMode={isMoney ? "numeric" : undefined}
          value={displayValue}
          onChange={handleChange}
          placeholder={field.placeholder || ""}
          min={isMoney ? undefined : field.min}
          max={isMoney ? undefined : field.max}
          step={isMoney ? undefined : field.step}
          className={`${inputCls} ${field.suffix ? "pr-9" : ""}`}
          data-testid={`detail-${field.key}`}
        />
        {field.suffix && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-mono font-semibold text-zinc-500">
            {field.suffix}
          </span>
        )}
      </div>
    </div>
  );
}
